/* eslint-disable no-lonely-if */
/* eslint-disable consistent-return */

import { CSSProperties, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export function PickerPositioner({
  top,
  anchorBottom,
  left,
  children,
  visible = true,
}: {
  top: number;
  anchorBottom?: number;
  left: number;
  children: ReactNode;
  visible?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const bottomAnchor = anchorBottom ?? top;
  const [animOpacity, setAnimOpacity] = useState(0);

  useEffect(() => {
    if (!visible) {
      setAnimOpacity(0);
      return;
    }
    const id = requestAnimationFrame(() => setAnimOpacity(1));
    return () => cancelAnimationFrame(id);
  }, [visible]);

  const [style, setStyle] = useState<CSSProperties>({
    position: 'fixed',
    top,
    left,
    transform: 'translateY(-100%) translateY(-8px)',
    zIndex: 1050,
    visibility: 'hidden',
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const calculate = () => {
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const MARGIN = 8;

      let newLeft = left;
      if (left + rect.width > vw - MARGIN) {
        newLeft = vw - rect.width - MARGIN;
      }
      if (newLeft < MARGIN) newLeft = MARGIN;

      const fitsAbove = top - rect.height - MARGIN >= 0;
      const fitsBelow = bottomAnchor + 8 + rect.height + MARGIN <= vh;

      let transform: string;
      let newTop: number;

      if (fitsAbove) {
        transform = 'translateY(-100%) translateY(-8px)';
        newTop = top;
      } else if (fitsBelow) {
        transform = 'translateY(8px)';
        newTop = bottomAnchor;
      } else {
        if (top >= vh - bottomAnchor) {
          transform = 'translateY(-100%) translateY(-8px)';
          newTop = Math.max(top, rect.height + MARGIN);
        } else {
          transform = 'translateY(8px)';
          newTop = Math.min(bottomAnchor, vh - rect.height - 8 - MARGIN);
        }
      }

      if (!visible) {
        setStyle({
          position: 'fixed',
          top: newTop,
          left: newLeft,
          transform,
          zIndex: 1050,
          visibility: 'hidden',
          pointerEvents: 'none',
        });
        return;
      }

      setStyle({
        position: 'fixed',
        top: newTop,
        left: newLeft,
        transform,
        zIndex: 1050,
        visibility: 'visible',
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '4px',
        boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
        transition: 'opacity 0.15s ease',
      });
    };

    calculate();

    const ro = new ResizeObserver(calculate);
    ro.observe(el);
    return () => ro.disconnect();
  }, [top, bottomAnchor, left, visible]);

  return createPortal(
    <div ref={ref} style={{ ...style, opacity: animOpacity }}>
      {children}
    </div>,
    document.body
  );
}
